/**
 * Entity constructors.
 *
 * Every entity is created here, never by an object literal at a call site. Two reasons: a field
 * added to `MapNode` becomes a compile error in exactly one file instead of twenty, and the
 * defaults that make a node valid live somewhere a reader can find them.
 *
 * Ids and timestamps are parameters, not generated inside. A factory that calls `Date.now()` or
 * a random id generator cannot be tested deterministically, and the command layer needs to mint
 * both anyway so the same operation replays identically from the journal.
 */
import { DEFAULT_NODE_SHAPE, DEFAULT_NODE_TYPE, } from '@orbit-mind/shared-types';
import { DEFAULT_MAP_SETTINGS } from './entities';
import { firstOrder } from './ordering';
import { richTextFromPlain, toPlainText } from './richText';
/** The schema version new documents are written at. Bumped only alongside a migration. */
export const CURRENT_SCHEMA_VERSION = 1;
export const DEFAULT_VIEWPORT = { x: 0, y: 0, zoom: 1 };
export function createNode(input) {
    // `richText` wins when both are given; `text` is the shorthand for the common case of typing
    // a label. Deriving `plainText` here rather than accepting it is what keeps the two in step.
    const richText = input.richText ?? richTextFromPlain(input.text ?? '');
    const plainText = toPlainText(richText);
    return {
        id: input.id,
        mapId: input.mapId,
        parentId: input.parentId,
        order: input.order,
        side: input.side ?? 'auto',
        type: input.type ?? DEFAULT_NODE_TYPE,
        title: plainText.split('\n')[0] ?? '',
        richText,
        plainText,
        // The caller's position, or the origin. Never a guess: the layout engine owns geometry, and
        // a factory inventing coordinates would put a node somewhere nothing had decided on.
        x: input.position?.x ?? 0,
        y: input.position?.y ?? 0,
        // Sized by measurement at render time until a layout assigns extents — see `buildScene`.
        width: 0,
        height: 0,
        rotation: 0,
        zIndex: 0,
        collapsed: false,
        locked: false,
        hidden: false,
        shape: DEFAULT_NODE_SHAPE,
        style: {},
        createdAt: input.now,
        createdBy: input.by,
        updatedAt: input.now,
        updatedBy: input.by,
    };
}
export function createMap(input) {
    return {
        id: input.id,
        workspaceId: input.workspaceId,
        projectId: null,
        folderId: null,
        ownerId: input.ownerId,
        title: input.title ?? 'Untitled map',
        rootNodeId: input.rootNodeId,
        schemaVersion: CURRENT_SCHEMA_VERSION,
        layoutId: input.layoutId ?? 'curved-line-tree/left-right',
        themeId: input.themeId ?? 'modern-light',
        viewport: DEFAULT_VIEWPORT,
        settings: DEFAULT_MAP_SETTINGS,
        // A brand-new map has one unsynced edit: its own creation.
        localRevision: 1,
        serverRevision: 0,
        createdAt: input.now,
        createdBy: input.ownerId,
        updatedAt: input.now,
        updatedBy: input.ownerId,
        deletedAt: null,
    };
}
export function createEdge(input) {
    const edge = {
        id: input.id,
        mapId: input.mapId,
        sourceNodeId: input.sourceNodeId,
        targetNodeId: input.targetNodeId,
        relationshipType: 'relates-to',
        lineType: 'dashed',
        routingType: 'curved',
        startMarker: 'none',
        // Cross-links are directional by default: a reference from A to B usually means something
        // the reverse does not, and a plain line loses that.
        endMarker: 'arrow',
        animated: false,
        animationType: 'none',
        createdAt: input.now,
        updatedAt: input.now,
    };
    return input.label === undefined ? edge : { ...edge, label: input.label };
}
export function createAttachment(input) {
    return {
        id: input.id,
        workspaceId: input.workspaceId,
        mapId: input.mapId,
        nodeId: input.nodeId,
        type: attachmentTypeFor(input.mimeType),
        filename: input.filename,
        mimeType: input.mimeType,
        size: input.size,
        storageKey: input.storageKey,
        checksum: input.checksum,
        status: 'pending',
        ...(input.metadata === undefined ? {} : { metadata: input.metadata }),
        createdAt: input.now,
        createdBy: input.by,
    };
}
/**
 * What kind of thing a MIME type describes.
 *
 * Coarse on purpose. The catalogue has twelve types and this maps the ones a browser file picker
 * actually produces; everything unrecognised is `other`, which is a thing the UI can render
 * rather than a case it has to guard against.
 */
export function attachmentTypeFor(mimeType) {
    if (mimeType.startsWith('image/'))
        return 'image';
    if (mimeType.startsWith('video/'))
        return 'video';
    if (mimeType.startsWith('audio/'))
        return 'audio';
    if (mimeType === 'application/pdf')
        return 'pdf';
    if (mimeType.startsWith('text/'))
        return 'text';
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel'))
        return 'spreadsheet';
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint'))
        return 'presentation';
    if (mimeType.includes('word') || mimeType.includes('document'))
        return 'document';
    return 'other';
}
/** A complete, valid one-node document — what "new map" produces. */
export function createDocument(input) {
    const map = createMap({
        id: input.mapId,
        workspaceId: input.workspaceId,
        rootNodeId: input.rootNodeId,
        ownerId: input.ownerId,
        now: input.now,
        ...(input.title !== undefined ? { title: input.title } : {}),
        ...(input.layoutId !== undefined ? { layoutId: input.layoutId } : {}),
        ...(input.themeId !== undefined ? { themeId: input.themeId } : {}),
    });
    const root = createNode({
        id: input.rootNodeId,
        mapId: input.mapId,
        parentId: null,
        order: firstOrder(),
        now: input.now,
        by: input.ownerId,
        text: input.rootText ?? input.title ?? 'Central idea',
    });
    return { map, nodes: { [input.rootNodeId]: root }, edges: {} };
}
//# sourceMappingURL=factories.js.map